// MonoPcmRecorder.ts
// Stand‑alone browser module to record microphone audio into a mono PCM WAV Blob.
// Provides a singleton interface that can be put to sleep (release mic) and woken up (re‑acquire mic).
// Usage (ESM):
//   import Recorder from "./MonoPcmRecorder";
//   await Recorder.wake();
//   await Recorder.start();
//   ... await Recorder.stop();
//   const wavBlob = Recorder.getBlob();
//
//   await Recorder.sleep(); // release microphone when other code needs it

/* eslint‑disable no‑async‑promise‑executor */

export type RecorderState = "idle" | "recording" | "paused" | "sleeping";

interface RecorderInterface {
  /** Ensure mic is acquired and audio context is ready */
  wake(): Promise<void>;
  /** Release mic & audio context; no active listeners */
  sleep(): Promise<void>;
  /** Start capturing into internal buffer */
  start(): Promise<void>;
  /** Stop capture and finalise WAV Blob */
  stop(): Promise<Blob>;
  /** Retrieve last captured WAV Blob (or null) */
  getBlob(): Blob | null;
  /** Current state */
  state: RecorderState;
}

class MonoPcmRecorder implements RecorderInterface {
  /* Singleton instance created at bottom */

  public state: RecorderState = "idle";

  private _audioCtx: AudioContext | null = null;
  private _sourceNode: MediaStreamAudioSourceNode | null = null;
  private _workletNode: AudioWorkletNode | null = null;
  private _mediaStream: MediaStream | null = null;

  private _sampleRate = 48000; // default; updated from AudioContext
  private _recordedSamples: Int16Array[] = [];
  private _wavBlob: Blob | null = null;

  /**
   * Initialise / re‑initialise audio context and worklet, requesting user mic if necessary
   */
  public async wake(): Promise<void> {
    if (this.state !== "sleeping" && this._audioCtx) {
      return; // already awake
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("getUserMedia is not supported in this browser");
    }
    this._mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    this._audioCtx = new AudioContext({ sampleRate: 16000 });
    this._sampleRate = this._audioCtx.sampleRate;

    // Dynamically add an AudioWorkletProcessor that posts raw samples to main thread
    // Worklet code as Blob -> URL to avoid extra files
    const workletCode = `class PCMWorkletProcessor extends AudioWorkletProcessor {
      constructor() {
        super();
      }
      process(inputs) {
        const input = inputs[0];
        if (input && input[0]) {
          // Post Float32Array for mono channel
          this.port.postMessage(input[0]);
        }
        return true;
      }
    }
    registerProcessor('pcm-worklet', PCMWorkletProcessor);`;

    const blobURL = URL.createObjectURL(new Blob([workletCode], { type: 'application/javascript' }));
    await this._audioCtx.audioWorklet.addModule(blobURL);

    this._sourceNode = this._audioCtx.createMediaStreamSource(this._mediaStream);
    this._workletNode = new AudioWorkletNode(this._audioCtx, 'pcm-worklet');

    this._workletNode.port.onmessage = (event) => {
      const floatChunk = event.data as Float32Array;
      const int16Chunk = this._float32ToInt16(floatChunk);
      this._recordedSamples.push(int16Chunk);
    };

    this._sourceNode.connect(this._workletNode);
    // WorkletNode need not connect to destination when just harvesting data
    this.state = "idle";
  }

  /**
   * Stop audio graph & mic, release resources
   */
  public async sleep(): Promise<void> {
    if (this.state === "sleeping") return;

    if (this._workletNode) {
      this._workletNode.port.onmessage = null;
      this._workletNode.disconnect();
      this._workletNode = null;
    }
    if (this._sourceNode) {
      this._sourceNode.disconnect();
      this._sourceNode = null;
    }
    if (this._mediaStream) {
      this._mediaStream.getTracks().forEach((t) => t.stop());
      this._mediaStream = null;
    }
    if (this._audioCtx) {
      await this._audioCtx.close();
      this._audioCtx = null;
    }
    this.state = "sleeping";
  }

  /** Begin capture */
  public async start(): Promise<void> {
    if (this.state === "recording") return;
    if (!this._audioCtx || !this._sourceNode) {
      await this.wake();
    }
    
    // Enhanced cleanup for second recording issues
    this._recordedSamples = [];
    this._wavBlob = null;
    
    console.log('PcmRecorder start: Cleared samples and blob, starting fresh recording');
    this.state = "recording";
  }

  /** Stop capture and encode to WAV */
  public async stop(): Promise<Blob> {
    if (this.state !== "recording") {
      throw new Error("Recorder is not recording");
    }
    this.state = "idle";
    const combined = this._mergeBuffers(this._recordedSamples);
    const wavBuffer = this._encodeWAV(combined, this._sampleRate);
    this._wavBlob = new Blob([wavBuffer], { type: "audio/wav" });
    return this._wavBlob;
  }

  public getBlob(): Blob | null {
    return this._wavBlob;
  }

  /* ----------- helpers ------------- */
  private _float32ToInt16(buffer: Float32Array): Int16Array {
    const l = buffer.length;
    const out = new Int16Array(l);
    for (let i = 0; i < l; i++) {
      const s = Math.max(-1, Math.min(1, buffer[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }

  private _mergeBuffers(chunks: Int16Array[]): Int16Array {
    const length = chunks.reduce((sum, c) => sum + c.length, 0);
    const result = new Int16Array(length);
    let offset = 0;
    for (const chunk of chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }

  private _encodeWAV(samples: Int16Array, sampleRate: number): ArrayBuffer {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);

    /* RIFF identifier */ this._writeString(view, 0, "RIFF");
    /* RIFF chunk length */ view.setUint32(4, 36 + samples.length * 2, true);
    /* RIFF type */ this._writeString(view, 8, "WAVE");

    /* format chunk identifier */ this._writeString(view, 12, "fmt ");
    /* format chunk length */ view.setUint32(16, 16, true);
    /* sample format (raw) */ view.setUint16(20, 1, true);
    /* channel count */ view.setUint16(22, 1, true); // mono
    /* sample rate */ view.setUint32(24, sampleRate, true);
    /* byte rate (sample rate * block align) */ view.setUint32(28, sampleRate * 2, true);
    /* block align (channel count * bytes per sample) */ view.setUint16(32, 2, true);
    /* bits per sample */ view.setUint16(34, 16, true);

    /* data chunk identifier */ this._writeString(view, 36, "data");
    /* data chunk length */ view.setUint32(40, samples.length * 2, true);

    // Write PCM samples
    let offset = 44;
    for (let i = 0; i < samples.length; i++, offset += 2) {
      view.setInt16(offset, samples[i], true);
    }

    console.log('👉', 'WAV encoded:', samples.length, 'samples', 'at', sampleRate, 'Hz', 'duration:', samples.length / sampleRate, 'seconds');

    return buffer;
  }

  private _writeString(view: DataView, offset: number, str: string): void {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  }
}

// Export singleton instance
const PcmRecorder = new MonoPcmRecorder();
export default PcmRecorder;
